import { Expose, Exclude, Transform, Type } from 'class-transformer'
import { DocumentSnapshot } from 'firebase/firestore'
import { _FirebaseDocument, anyToDate, toDocRef, toDocRefArray, DocRef } from './Models'
import type { ReviewReturned } from './assessmentViews'
import type { TaskReviewDecision, TaskProgressStatus, TaskSource } from './Task'
import { V2Context, V2Clause, V2Document, SourceQuestion, SourceRequirement, progressOrTodo } from './Task'

@Exclude()
export class ProjectTask extends _FirebaseDocument {
  // Core task fields (copied from assessment task on accept)
  @Expose()
  public name?: string
  @Expose()
  public priority?: string
  @Expose()
  public category?: string
  @Expose()
  public description?: string
  @Expose()
  @Type(() => V2Context)
  public context?: V2Context | null
  @Expose({ name: 'clauses_v2' })
  @Type(() => V2Clause)
  public clausesV2?: V2Clause[]
  @Expose({ name: 'documents_v2' })
  @Type(() => V2Document)
  public documentsV2?: V2Document[]
  @Expose({ name: 'source_questions' })
  @Type(() => SourceQuestion)
  public sourceQuestions?: SourceQuestion[]
  /** The requirements a referential task answers to — MYAC-1093. */
  @Expose({ name: 'source_requirements' })
  @Type(() => SourceRequirement)
  public sourceRequirements?: SourceRequirement[]

  // Review state
  @Expose()
  public review_decision?: TaskReviewDecision | null
  @Expose()
  public review_progress?: TaskProgressStatus | null
  @Expose()
  public review_assignee_uid?: string | null
  @Expose()
  public review_rejection_comment?: string | null
  /** MYAC-1639 — which set-aside reason was picked (`rejectionReason.ts`); absent on
   *  every rejection written before it. Only beside `review_decision: 'rejected'`. */
  @Expose()
  public review_rejection_reason?: string | null
  /** MYAC-1089 — end of the rejection's period (a Timestamp in Firestore);
   *  absent = no end. Only beside `review_decision: 'rejected'`. */
  @Expose()
  @Transform(anyToDate)
  public review_rejected_until?: Date | null
  /** MYAC-1089 — the note the daily sweep leaves when the period ran out and the
   *  task came back to review; cleared by the next human ruling. */
  @Expose()
  public review_returned?: ReviewReturned | null

  // Source traceability
  @Expose()
  public source_assessment_ref?: string | null
  @Expose()
  public source_type?: string | null
  /**
   * MYAC-1166 — stamped by the apply layer once an AI-authored edit (or an
   * AI-actor create) landed on this task. The dialog title's AI label reads it
   * through `Task.isAiAuthored`; like every field here it has to be `@Expose`d
   * AND copied by `_resolveFromProjectTask`, or it never reaches the board —
   * which is exactly how it was missed (Aymeric, verification 2026-10-15).
   */
  @Expose()
  public ai_edited?: boolean | null
  /**
   * K4 (MYAC-776) — which ENGINE wrote the task, verbatim (`Task.source`).
   *
   * The class is `@Exclude()`d, so a field without `@Expose()` is dropped at
   * parse time — which is how the gap analysis's stamp, written by the task
   * lane on every task it creates, never reached the board: the filter the
   * analysis links to matched 0 of 140 cards on a product where 26 groups had
   * just been submitted.
   */
  @Expose()
  public source?: TaskSource | null

  /**
   * Which resolution strategy this task's proposal needs (IR-126 §4.3, X.7).
   *
   * Stamped at genesis by `apply_create`, and backfilled by the heal on first
   * touch (§5.8) — so absence no longer means "legacy", it means the task has
   * never been through the apply layer at all, or has no proposal by design.
   *
   * A plain string for the same reason as {@link lifecycle}: the server may add
   * a strategy this build has never heard of, and a union would turn that into
   * a parse error on a document we are otherwise happy to render.
   */
  @Expose()
  public proposal_locator?: string | null

  /**
   * A task typed by a human rather than approved from a proposal.
   *
   * The pair matters, not either half. `source_type: manual` alone is the
   * intent; a missing `proposal_locator` alone is also true of a task the heal
   * has not reached yet. Only together do they mean "there is nothing to send
   * this back to, and there never will be" — which is the state §4.3 makes
   * permanent and archive is the exit from.
   */
  get isManual(): boolean {
    return this.source_type === 'manual' && !this.proposal_locator
  }

  /**
   * The OCC anchor (IR-126 §5.1) — the seq of the task's latest revision.
   *
   * Absent on every task written before phase 0.2, and there is no backfill, so
   * this is read through `revisionSeq` rather than directly. Optional here
   * deliberately: a tolerant reader must not invent a value it did not see.
   */
  @Expose()
  public last_revision_seq?: number

  /**
   * Where the task is in its life (IR-126 §3.2, §5.7).
   *
   * `active` · `unapproved` (sent back, kept) · `parked` (MYAC-1639: the hidden
   * task of an action-plan row nobody has added yet) · `archived` (terminal).
   *
   * **Optional, and absent on every task written before phase 0.2** — there was
   * no backfill, so most existing tasks carry no such field. Read it through
   * {@link isOnBoard} rather than directly, which treats absent as `active`:
   * that is the model's own default server-side, and a tolerant reader must
   * not invent a value it did not see.
   *
   * Typed as a plain string, not a union. The server may add a lifecycle this
   * build has never heard of, and a union would make that a parse error on a
   * document we are otherwise happy to render — the same reason the Python
   * enums degrade to `UNKNOWN` rather than raising.
   */
  @Expose()
  public lifecycle?: string | null

  /**
   * The lifecycle a task was archived from, stamped by `task_archive`
   * (MYAC-1639). `parked` means it was never accepted: the hidden task of an
   * action a re-assessment dropped. Absent on every task archived before it.
   */
  @Expose()
  public archived_from?: string | null

  // TD-specific
  @Expose()
  public section_key?: string | null
  @Expose()
  public requirement_id?: string | null

  // Directive (for auto-rejected tasks)
  @Expose()
  public directive_id?: string | null
  @Expose()
  public directive_match_reason?: string | null

  // Legacy (dual-read period)
  @Expose()
  @Transform(toDocRef)
  public task_ref?: DocRef | null
  @Expose()
  @Transform(toDocRefArray)
  public task_grouping_subject_refs?: DocRef[]

  get isLegacy(): boolean {
    return !this.name && !!this.task_ref
  }

  get isAccepted(): boolean {
    return this.review_decision === 'accepted'
  }

  get isDirectiveRejected(): boolean {
    return this.review_decision === 'directive_rejected'
  }

  get effectiveProgress(): TaskProgressStatus {
    return progressOrTodo(this.review_progress)
  }

  /**
   * The server's revision counter, defaulting to 0 when the field is absent.
   *
   * 0 is the right default and not merely a convenient one: the apply layer
   * allocates `seq = base_seq + 1` and a task that has never been revised is on
   * seq 0, so a pre-0.2 document and a freshly created one are genuinely the
   * same state. Reading `last_revision_seq` directly and getting `undefined`
   * would send `base_seq: undefined`, which the apply layer refuses.
   */
  get revisionSeq(): number {
    return this.last_revision_seq ?? 0
  }

  /**
   * Terminal (§5.7). `ARCHIVED` appears in no permitted set, so neither
   * creating nor reviving applies — there is nothing an accept can do.
   */
  get isArchived(): boolean {
    return this.lifecycle === 'archived'
  }

  /**
   * Already accepted and on the board, as far as **this build** can tell.
   *
   * Deliberately narrower than `!isSentBack && !isArchived`: it names the two
   * values that mean "settled", so a lifecycle the server adds later is NOT
   * one of them. That difference is the point. An unrecognised state routed
   * here would be silently ignored — the click doing nothing, which is the bug
   * MYAC-990 exists to fix, reproduced for every future state. Routed to the
   * command instead, the apply layer decides and says so.
   *
   * Absent means `active`: phase 0.2 added the field with that default and ran
   * no backfill, so most tasks carry nothing here.
   *
   * **There is deliberately no `isSentBack` beside this.** It would read as the
   * obvious predicate for routing an accept — "is this the one state
   * `task_reapprove` accepts" — and using it would silently drop unknown
   * lifecycles back into the ignored branch, with every test still passing
   * because none exercises a state this build cannot classify. Accept does not
   * ask which states the command takes; it asks which states it can rule out.
   */
  get isSettled(): boolean {
    return !this.lifecycle || this.lifecycle === 'active'
  }

  /**
   * Whether this task belongs on the Kanban board.
   *
   * **Before IR-126 phase 4.2 this question did not exist**: un-approving a
   * task DELETED its Kanban document, so the board's membership was decided by
   * whether the document was there at all. `task_unapprove` keeps the task —
   * that is the entire point of the operation, since the trail has to survive —
   * so something now has to say that a kept task is not a shown task.
   *
   * Absent lifecycle means `active`, because phase 0.2 added the field with
   * that default and ran no backfill. Reading absent as "not on the board"
   * would hide every task created before this feature.
   *
   * Anything unrecognised is also treated as on-board, deliberately: a task
   * this build cannot classify is better shown than silently hidden, and
   * hiding it would make a server-side addition look like data loss.
   *
   * `parked` is known and never on the board (MYAC-1639): it is the discussion
   * of an action nobody has added yet, and its row still reads "to decide".
   */
  get isOnBoard(): boolean {
    return (
      this.lifecycle !== 'unapproved' && this.lifecycle !== 'archived' && this.lifecycle !== 'parked'
    )
  }

  get projectId(): string {
    return this.parent_path.split('/').pop() ?? ''
  }

  collection(): string {
    return 'tasks'
  }

  static fromFirebase(o: DocumentSnapshot): ProjectTask | null {
    return _FirebaseDocument._fromFirebase(ProjectTask, o)
  }
}
